// Imports remain the same...
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import BrandCard from "./BrandCard";
import { ReactComponent as Down } from "../../images/down-arrow.svg";
import { mobileSizes } from "../../util/sizes";
import { useSelector } from "react-redux";
import en from "../../util/locales/en";
import ar from "../../util/locales/ar";
import useApi from "../../hooks/useApi";

const Brands = ({ sellerId }) => {
  const DESKTOP_ITEMS_PER_PAGE = 8;
  const MOBILE_ITEMS_PER_PAGE = 4;

  const checkIsMobile = () => window.innerWidth <= 768;
  const [isMobile, setIsMobile] = useState(checkIsMobile());
  const [page, setPage] = useState(1);
  const [allBrands, setAllBrands] = useState([]);
  const [displayedBrands, setDisplayedBrands] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const language = useSelector((state) => state.language);
  const t = language === "en" ? en : ar;
  const { get, loading } = useApi();

  const itemsPerPage = isMobile
    ? MOBILE_ITEMS_PER_PAGE
    : DESKTOP_ITEMS_PER_PAGE;

  const fetchBrands = async () => {
    try {
      if (sellerId) {
        const response = await get(`/brand`, {
          page,
          page_size: itemsPerPage,
          seller: sellerId,
        });
        if (response?.results) {
          setAllBrands((prev) =>
            page === 1 ? response.results : [...prev, ...response.results]
          );
          setHasMore(response.results.length === itemsPerPage);
        }
      } else {
        const home_brands = await get(`/car_detail/cars_by_brand`);
        setAllBrands(home_brands || []);
        setDisplayedBrands((home_brands || []).slice(0, itemsPerPage));
        setHasMore(true); // Always allow "Show More" until full list is shown
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  useEffect(() => {
    setPage(1);
    setAllBrands([]);
    fetchBrands();
  }, [itemsPerPage]);

  useEffect(() => {
    const onResize = () => {
      const newIsMobile = checkIsMobile();
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
        setPage(1);
        setAllBrands([]);
        setHasMore(true);
        fetchBrands();
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobile]);

  const handleToggleBrands = () => {
    if (sellerId) {
      // Backend pagination logic
      if (hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchBrands();
      } else {
        // Reset pagination
        setPage(1);
        fetchBrands(true); // Assume `true` clears data and resets to page 1
        setHasMore(true);
      }
    } else {
      if (hasMore) {
        const nextPage = page + 1;
        const newDisplay = allBrands.slice(0, nextPage * itemsPerPage);
        setDisplayedBrands(newDisplay);
        setPage(nextPage);

        if (newDisplay.length >= allBrands.length) {
          setHasMore(false);
        }
      } else {
        // Show less (reset to first page)
        const resetDisplay = allBrands.slice(0, itemsPerPage);
        setDisplayedBrands(resetDisplay);
        setPage(1);
        setHasMore(true);

        const element = document.getElementById("header");
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const brandsToRender = sellerId ? allBrands : displayedBrands;

  return (
    <StyledSection
      className="home-brands-section"
      language={language}
      showMore={!hasMore}
    >
      <div className="section-header" id="header">
        {isMobile ? t.brands.mobileTitle : t.brands.title}
      </div>

      <div className="brands">
        {loading && page === 1
          ? Array.from({ length: itemsPerPage }).map((_, i) => (
              <LoadingBrandCard key={i} />
            ))
          : brandsToRender.map((brand, index) => (
              <Link
                to="/cars"
                state={{ brand: brand?.id || brand?.name }}
                className="brand"
                key={index}
              >
                <BrandCard brand={brand} isSeller={sellerId} />
              </Link>
            ))}
      </div>

      {!loading && brandsToRender.length >= itemsPerPage && (
        <div className="show-more">
          <button onClick={handleToggleBrands} disabled={loading}>
            <Down />
            <p>{loading ? t.loading : !hasMore ? t.showLess : t.showMore}</p>
          </button>
        </div>
      )}
    </StyledSection>
  );
};

const StyledSection = styled.div`
  direction: ${(props) => (props.language == "en" ? "ltr" : "rtl")};
  padding: calc(27 / 1440 * 100vw) calc(40 / 1440 * 100vw);
  background-color: #fff;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 4px 14px;
  width: calc(1248 / 1440 * 100vw);

  @media (max-width: 768px) {
    gap: 0.556vw;
    padding: calc(20 / 375 * 100vw) calc(16 / 375 * 100vw);
    inset-inline-start: calc(20 / 375 * 100vw);
  }

  .brands {
    margin-top: 2.222vw;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 2.222vw 1.944vw;
  }

  .show-more {
    margin-top: 2.361vw;
    width: 100%;
    display: flex;
    justify-content: center;

    button {
      background: none;
      border: none;
      outline: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 0.556vw;

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }

      p {
        font-family: ${(props) =>
          props.language == "en" ? "Poppins" : "Almarai"};
        font-style: normal;
        font-weight: 700;
        font-size: 1.389vw;
        line-height: 1.458vw;
        color: #154cd6;

        @media (min-width: 1440px) {
          font-size: 20px;
          line-height: 21px;
        }
      }
    }

    svg {
      width: 1.667vw;
      height: 1.667vw;
      transform: ${(props) => (props.showMore ? "scaleY(-1)" : "none")};

      @media (min-width: 1440px) {
        width: 24px;
        height: 24px;
      }
    }
  }

  @media (max-width: 768px) {
    padding: ${mobileSizes._20px} ${mobileSizes._16px};

    .brands {
      margin-top: ${mobileSizes._16px};
      grid-template-columns: 1fr 1fr;
      gap: ${mobileSizes._8px} ${mobileSizes._8px};
    }

    .show-more {
      margin-top: ${mobileSizes._16px};

      button {
        gap: 0.556vw;

        p {
          font-weight: 600;
          font-size: ${mobileSizes._14px};
          line-height: ${mobileSizes._21px};
        }
      }

      svg {
        width: ${mobileSizes._24px};
        height: ${mobileSizes._24px};
      }
    }
  }
`;

const LoadingBrandCard = styled.div`
  width: 18.264vw;
  height: 5.556vw;
  display: flex;
  align-items: center;
  gap: 1.389vw;
  background: #f6f6f6;
  border-radius: 0.278vw;
  padding: 1.111vw 1.806vw;
  border: 0.069vw solid #eee;
  animation: pulse 1.5s infinite ease-in-out;

  .logo {
    width: 2.778vw;
    height: 2.778vw;
    background: #ddd;
    border-radius: 50%;
  }

  .info {
    flex: 1;

    .name,
    .cars-count {
      height: 1.25vw;
      background: #ddd;
      border-radius: 0.2vw;
      margin-bottom: 0.5vw;
    }

    .name {
      width: 10vw;
    }

    .cars-count {
      width: 6vw;
    }
  }

  @keyframes pulse {
    0% {
      background-color: #f6f6f6;
    }
    50% {
      background-color: #e9e9e9;
    }
    100% {
      background-color: #f6f6f6;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    padding: ${mobileSizes._16px};
    gap: ${mobileSizes._8px};

    .logo {
      width: ${mobileSizes._40px};
      height: ${mobileSizes._40px};
    }

    .info {
      .name,
      .cars-count {
        height: ${mobileSizes._12px};
        margin-bottom: ${mobileSizes._4px};
      }
    }
  }
`;

export default Brands;
